/**
 * Lazy text loader for a FileSource.
 *
 * Used by text-oriented renderers (markdown, code, csv). Kicks off
 * `source.getText()` on mount and tracks the loading / error / data
 * tri-state so renderers can render their own spinners + error states
 * without each one rolling its own fetch boilerplate.
 *
 * Re-runs when the source identity (`source.id`) changes so a dialog
 * that swaps between sources without unmounting still fetches afresh.
 */
import { useEffect, useState } from "react";

import type { FileSource } from "../types";

export type FileTextState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; text: string }
  | { status: "error"; error: Error };

export function useFileText(source: FileSource): FileTextState {
  const [state, setState] = useState<FileTextState>({ status: "idle" });

  /**
   * Key the effect on `source.id` rather than the object identity.
   * Callers that rebuild a source object every render (factories return
   * fresh objects) would otherwise refetch on every parent re-render,
   * which is both wasteful and visually disruptive (loading spinner
   * flashes). `source.id` is the source's own stable identity.
   */
  useEffect(() => {
    /**
     * Sources without `getText` can't satisfy text renderers — surface that
     * as an error rather than spinning forever, so the renderer can fall
     * back to its unsupported state instead of looking hung.
     */
    if (!source.getText) {
      setState({
        status: "error",
        error: new Error("Source does not support text access"),
      });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    source
      .getText()
      .then((text) => {
        if (!cancelled) setState({ status: "ready", text });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ status: "error", error });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: see comment above
  }, [source.id]);

  return state;
}
